import { useState } from "react";

// Gestione degli slot fissi (bookingType 'fixedSlots').
// La disponibilità è INTERAMENTE manuale: il sistema non decrementa i posti alle prenotazioni.
// L'admin aggiorna qui "posti residui" e "sold out" dopo ogni conferma; uno slot sold-out
// (o con 0 posti residui) non è più selezionabile dagli utenti e viene rifiutato dal server.
// "Non disponibile" (isUnavailable) è lo stato distinto dal sold-out — lo slot non viene
// offerto (es. annullato dall'organizzatore): anch'esso non selezionabile e rifiutato dal server.
// Data e orari di uno slot esistente non sono modificabili (rimuovere e ricreare): le richieste
// già inviate ne conservano comunque uno snapshot.

const EMPTY_SLOT = { date: '', startTime: '', endTime: '', maxParticipants: '' };

const formatSlotDate = (isoDate) => {
    const parts = isoDate.split('-');
    return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : isoDate;
};

const SlotsEditor = ({ inputData, setInputData }) => {
    const [slotInput, setSlotInput] = useState(EMPTY_SLOT);

    const handleSlotInput = (event) => {
        const { id, value } = event.target;
        setSlotInput(prevState => ({
            ...prevState,
            [id.replace('slot-', '')]: value
        }));
    };

    const canAdd = slotInput.date !== '' && slotInput.startTime !== '';

    const addSlot = () => {
        const maxParticipants = slotInput.maxParticipants === '' ? null : Number(slotInput.maxParticipants);
        const newSlot = {
            date: slotInput.date,
            startTime: slotInput.startTime,
            endTime: slotInput.endTime !== '' ? slotInput.endTime : null,
            maxParticipants,
            remainingSpots: maxParticipants, // parte pieno; poi lo aggiorna l'admin a mano
            isSoldOut: false,
            isUnavailable: false
        };
        setInputData(prevState => ({
            ...prevState,
            slots: [...prevState.slots, newSlot].sort((a, b) =>
                `${a.date}T${a.startTime}`.localeCompare(`${b.date}T${b.startTime}`))
        }));
        setSlotInput(EMPTY_SLOT);
    };

    const updateSlot = (index, patch) => {
        setInputData(prevState => ({
            ...prevState,
            slots: prevState.slots.map((element, i) => i === index ? { ...element, ...patch } : element)
        }));
    };

    const removeSlot = (index) => {
        setInputData(prevState => ({
            ...prevState,
            slots: prevState.slots.filter((element, i) => i !== index)
        }));
    };

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-col gap-3 p-3 border-2 rounded">
                {/* md:items-end: gli input restano allineati sulla stessa base anche se
                    un'etichetta dovesse andare a capo in una colonna stretta */}
                <div className="flex flex-col md:flex-row md:items-end gap-2">
                    <div className="flex flex-col gap-2 flex-1">
                        <label htmlFor="slot-date" className="whitespace-nowrap">Data</label>
                        <input type="date" id="slot-date" value={slotInput.date} onChange={handleSlotInput} />
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                        <label htmlFor="slot-startTime" className="whitespace-nowrap">Inizio</label>
                        <input type="time" id="slot-startTime" value={slotInput.startTime} onChange={handleSlotInput} />
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                        <label htmlFor="slot-endTime" className="whitespace-nowrap">Fine (facoltativa)</label>
                        <input type="time" id="slot-endTime" value={slotInput.endTime} onChange={handleSlotInput} />
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                        <label htmlFor="slot-maxParticipants" className="whitespace-nowrap">Posti massimi (facoltativi)</label>
                        <div className="flex gap-2">
                            <input type="number" min="1" id="slot-maxParticipants" className="flex-grow w-full" value={slotInput.maxParticipants} onChange={handleSlotInput} />
                            {canAdd && <button className="w-[72px] h-[72px] rounded-lg border-2 border-[#225424] text-[#225424] flex justify-center items-center text-4xl pb-1 shrink-0" onClick={addSlot}>+</button>}
                            {!canAdd && <button className="w-[72px] h-[72px] rounded-lg border-2 border-neutral-300 text-neutral-300 flex justify-center items-center text-4xl pb-1 cursor-not-allowed shrink-0">+</button>}
                        </div>
                    </div>
                </div>
            </div>
            <div className="border-2 p-2 flex flex-col gap-2 min-h-[72px] rounded">
                {
                    inputData && inputData.slots && inputData.slots.length > 0 && inputData.slots.map((element, index) => {
                        const isEffectivelySoldOut = element.isSoldOut || element.remainingSpots === 0;
                        const isUnavailable = Boolean(element.isUnavailable);
                        return (
                            <div key={element._id || `new-${index}`} className={`border-2 py-2 px-3 rounded-lg flex flex-col gap-2 w-full ${isUnavailable ? 'border-neutral-400 bg-neutral-100' : isEffectivelySoldOut ? 'border-red-500 bg-red-50' : 'border-[#225424]'}`}>
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                                    <div className="text-lg">
                                        {formatSlotDate(element.date)} — {element.startTime}{element.endTime ? ` / ${element.endTime}` : ''}
                                        {isUnavailable && <span className="text-neutral-500 font-bold ml-2">NON DISPONIBILE</span>}
                                        {!isUnavailable && isEffectivelySoldOut && <span className="text-red-500 font-bold ml-2">SOLD OUT</span>}
                                    </div>
                                    <div className="flex items-center gap-4 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm">Posti residui:</label>
                                            <input
                                                type="number"
                                                min="0"
                                                className="w-20"
                                                value={element.remainingSpots ?? ''}
                                                onChange={(event) => updateSlot(index, { remainingSpots: event.target.value === '' ? null : Number(event.target.value) })}
                                            />
                                            {element.maxParticipants != null && <span className="text-sm">/ {element.maxParticipants}</span>}
                                        </div>
                                        <div className="pt-2"><i className="fi fi-sr-circle-xmark text-xl cursor-pointer" onClick={() => removeSlot(index)}></i></div>
                                    </div>
                                </div>
                                {/* I due stati manuali sotto agli orari (layout richiesto dal cliente 2026-07-21) */}
                                <div className="flex items-center gap-6 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(element.isSoldOut)}
                                            onChange={(event) => updateSlot(index, { isSoldOut: event.target.checked })}
                                        />
                                        <label className="text-sm">Sold out</label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={isUnavailable}
                                            onChange={(event) => updateSlot(index, { isUnavailable: event.target.checked })}
                                        />
                                        <label className="text-sm">Non disponibile</label>
                                    </div>
                                </div>
                            </div>
                        )
                    })
                }
            </div>
        </div>
    )
}

export default SlotsEditor;
